import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getData, postData, uploadFile } from '../../services/api';
import RegistrationPathSelector from '../../components/auth/RegistrationPathSelector';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5030/api').replace(/\/api\/?$/, '');

const resolvePublicAssetUrl = (url) => {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:')) return value;
  return `${API_BASE}${value.startsWith('/') ? value : `/${value}`}`;
};

export default function UserRegister() {
  const navigate = useNavigate();
  const { collegeSlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const prefilledCode = searchParams.get('tenant') || searchParams.get('code') || '';
  const prefilledCollegeCode = searchParams.get('collegeCode') || searchParams.get('college') || '';
  const hasSlugCollege = !!String(collegeSlug || '').trim();

  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantError, setTenantError] = useState('');
  const [orgCode, setOrgCode] = useState(prefilledCode);
  const [collegeAccessInfo, setCollegeAccessInfo] = useState(null);
  const [collegeAccessError, setCollegeAccessError] = useState('');

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    passedOutYear: '',
    department: '',
    collegeStudentId: '',
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registerResult, setRegisterResult] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpMaskedPhone, setOtpMaskedPhone] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [detailsStepComplete, setDetailsStepComplete] = useState(false);
  const [proofVerifiedMessage, setProofVerifiedMessage] = useState('');

  const activeCollegeCode = useMemo(
    () => String(collegeAccessInfo?.uniqueAccessCode || collegeAccessInfo?.code || '').trim(),
    [collegeAccessInfo]
  );

  useEffect(() => {
    if (hasSlugCollege) return undefined;
    if (!orgCode.trim()) {
      setTenantInfo(null);
      setTenantError('');
      return undefined;
    }
    if (orgCode.trim().length < 4) {
      setTenantInfo(null);
      setTenantError('');
      return undefined;
    }
    const timer = setTimeout(() => {
      getData(`/auth/resolve-tenant?slug=${encodeURIComponent(orgCode.trim())}`)
        .then((r) => {
          setTenantInfo(r?.data || r);
          setTenantError('');
        })
        .catch(() => {
          setTenantInfo(null);
          setTenantError('Organization not found. Check the code and try again.');
        });
    }, 600);
    return () => clearTimeout(timer);
  }, [orgCode, hasSlugCollege]);

  useEffect(() => {
    let active = true;

    if (hasSlugCollege) {
      const params = new URLSearchParams({ collegeSlug: String(collegeSlug) });
      if (prefilledCode) params.set('tenant', prefilledCode);
      if (prefilledCollegeCode) params.set('collegeCode', prefilledCollegeCode);
      getData(`/training/college-access/public/resolve-by-slug?${params.toString()}`)
        .then((res) => {
          const payload = res?.data || res || {};
          if (!active) return;
          setCollegeAccessInfo(payload.college || null);
          setCollegeAccessError('');
          if (payload.tenant) {
            setTenantInfo(payload.tenant);
            setOrgCode(String(payload.tenant.slug || ''));
            setTenantError('');
          }
        })
        .catch(() => {
          if (!active) return;
          setCollegeAccessInfo(null);
          setCollegeAccessError('College access link is invalid or inactive.');
        });
      return () => {
        active = false;
      };
    }

    setCollegeAccessInfo(null);
    setCollegeAccessError('');

    return () => {
      active = false;
    };
  }, [collegeSlug, hasSlugCollege, prefilledCode, prefilledCollegeCode]);

  useEffect(() => {
    if (!otpCooldown) return undefined;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  useEffect(() => {
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpMaskedPhone('');
    setOtpSuccessMessage('');
    setProofVerifiedMessage('');
  }, [orgCode, form.phone]);

  useEffect(() => {
    setDetailsStepComplete(false);
    setProofVerifiedMessage('');
  }, [collegeSlug, orgCode]);

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const hasPhoneNumber = !!form.phone.trim();
  const canSendOtp = !!orgCode.trim() && !tenantError && !!form.phone.trim() && !otpLoading && otpCooldown === 0;
  const isCollegePortal = hasSlugCollege;
  const cardTitle = isCollegePortal ? `Register with ${collegeAccessInfo?.name || 'College Access'}` : 'Create Account';
  const cardLead = isCollegePortal
    ? 'Use your student details and verify your phone number to complete registration.'
    : 'Join your organization and wait for the admin to assign your role.';
  const collegeLogo = resolvePublicAssetUrl(collegeAccessInfo?.logoUrl || '');

  const handleSendOtp = async () => {
    setError('');
    setOtpSuccessMessage('');
    if (!orgCode.trim()) {
      setError('Please enter your organization code.');
      return;
    }
    if (tenantError) {
      setError('Organization not found. Please check the code.');
      return;
    }
    if (!form.phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await postData('/auth/send-registration-otp', {
        organizationCode: orgCode.trim(),
        phone: form.phone,
      });
      const payload = res?.data || res;
      setOtpSent(true);
      setOtpVerified(false);
      setOtpMaskedPhone(payload.phone || '');
      setOtpCooldown(payload.resendCooldownSeconds || 60);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setOtpSuccessMessage('');
    if (otp.trim().length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setOtpVerifyLoading(true);
    try {
      await postData('/auth/verify-registration-otp', {
        organizationCode: orgCode.trim(),
        phone: form.phone,
        otp: otp.trim(),
      });
      setOtpVerified(true);
      setOtpSuccessMessage('Your OTP is verified. Now you can enter the password and create your account.');
    } catch (err) {
      setOtpVerified(false);
      setError(err.response?.data?.message || err.message || 'Failed to verify OTP.');
    } finally {
      setOtpVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!orgCode.trim()) {
      setError('Please enter your organization code.');
      return;
    }
    if (tenantError) {
      setError('Organization not found. Please check the code.');
      return;
    }
    if (hasPhoneNumber && !otpVerified) {
      setError('Please verify your phone number before creating the account.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (isCollegePortal && !proofFile) {
      setError('Please upload your college ID proof for verification.');
      return;
    }
    setLoading(true);
    try {
      let response = null;
      if (isCollegePortal) {
        const payload = new FormData();
        Object.entries(form).forEach(([key, value]) => payload.append(key, value || ''));
        payload.append('organizationCode', orgCode.trim());
        payload.append('collegeCode', activeCollegeCode);
        if (proofFile) payload.append('collegeIdProof', proofFile);
        response = await uploadFile('/auth/self-register', payload);
      } else {
        response = await postData('/auth/self-register', { ...form, organizationCode: orgCode.trim() });
      }
      setRegisterResult(response?.data || response || null);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsContinue = (e) => {
    e.preventDefault();
    setError('');
    setProofVerifiedMessage('');
    if (!form.firstName.trim()) {
      setError('First name is required.');
      return;
    }
    if (!form.lastName.trim()) {
      setError('Last name is required.');
      return;
    }
    if (!form.email.trim()) {
      setError('Email address is required.');
      return;
    }
    if (isCollegePortal) {
      if (!form.passedOutYear.trim()) {
        setError('College validation year is required.');
        return;
      }
      if (!form.department.trim()) {
        setError('Department is required.');
        return;
      }
      if (!proofFile) {
        setError('Please upload your college ID proof for validation.');
        return;
      }
      setProofVerifiedMessage('Your college ID proof was uploaded. Verification will run after registration submission.');
    }
    setDetailsStepComplete(true);
  };

  if (success) {
    const collegeAuthorizationStatus = String(registerResult?.collegeAuthorizationStatus || '').toLowerCase();
    const isAuthorizedCollegeAccess = isCollegePortal && collegeAuthorizationStatus === 'authorized';
    const isPendingCollegeAccess = isCollegePortal && collegeAuthorizationStatus === 'pending_review';
    return (
      <div className="auth-glass" style={{ maxWidth: 640, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>Done</div>
        <h5 className="fw-bold mb-2">Account Created</h5>
        <p style={{ color: '#cbd5e1', fontSize: 14 }}>
          {isAuthorizedCollegeAccess
            ? `Your account has been created for ${collegeAccessInfo?.name || 'your college portal'}. Your college ID proof was verified automatically and you can log in now.`
            : isPendingCollegeAccess
            ? `Your account has been created for ${collegeAccessInfo?.name || 'your college portal'}. Your college ID proof is pending admin verification. You can log in only after approval.`
            : isCollegePortal
            ? `Your account has been created for ${collegeAccessInfo?.name || 'your college portal'}.`
            : <>Your account has been created. An admin at <strong>{tenantInfo?.name || 'your organization'}</strong> will assign your role. You can log in once that is done.</>}
        </p>
        <button className="btn btn-primary w-100 mt-2" onClick={() => navigate(orgCode ? `/login?tenant=${encodeURIComponent(orgCode)}` : '/login')}>
          <i className="bi bi-box-arrow-in-right me-2"></i>Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="auth-glass" style={{ maxWidth: isCollegePortal ? 760 : 560 }}>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
        <div style={{ minWidth: 0 }}>
          <h4 className="fw-bold mb-1">{cardTitle}</h4>
          <p className="mb-0" style={{ fontSize: 13, color: '#cbd5e1' }}>{cardLead}</p>
        </div>
        {isCollegePortal && (
          <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ borderRadius: 14, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
            {collegeLogo ? (
              <img src={collegeLogo} alt={collegeAccessInfo?.name || 'College'} style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 10 }} />
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ width: 42, height: 42, borderRadius: 10, background: 'rgba(96,165,250,0.18)' }}>
                <i className="bi bi-mortarboard-fill"></i>
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{collegeAccessInfo?.name}</div>
              <div style={{ color: '#bfdbfe', fontSize: 11 }}>College partner</div>
            </div>
          </div>
        )}
      </div>

      <RegistrationPathSelector
        currentCollegeSlug={collegeSlug}
        tenantSlug={prefilledCode || tenantInfo?.slug || ''}
      />

      {collegeAccessError && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
          <i className="bi bi-exclamation-circle-fill"></i>{collegeAccessError}
        </div>
      )}
      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
          <i className="bi bi-exclamation-circle-fill"></i>{error}
        </div>
      )}
      {otpSuccessMessage && (
        <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
          <i className="bi bi-patch-check-fill"></i>{otpSuccessMessage}
        </div>
      )}

      {proofVerifiedMessage && (
        <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
          <i className="bi bi-card-checklist"></i>{proofVerifiedMessage}
        </div>
      )}

      <form onSubmit={detailsStepComplete ? handleSubmit : handleDetailsContinue}>
        {!orgCode && (
          <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13, borderRadius: 9 }}>
            <i className="bi bi-exclamation-circle-fill"></i>Registration link is missing tenant mapping. Open this page only through the shared college or organization link.
          </div>
        )}

        {!detailsStepComplete ? (
          <>
            <div className="row g-3 mb-1">
              <div className="col-12 col-md-6">
                <label className="form-label" style={{ fontSize: 13 }}>First Name <span className="text-danger">*</span></label>
                <input className="form-control" placeholder="First name" value={form.firstName} onChange={(e) => updateForm('firstName', e.target.value)} required />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label" style={{ fontSize: 13 }}>Last Name <span className="text-danger">*</span></label>
                <input className="form-control" placeholder="Last name" value={form.lastName} onChange={(e) => updateForm('lastName', e.target.value)} required />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label" style={{ fontSize: 13 }}>Email Address <span className="text-danger">*</span></label>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-envelope"></i></span>
                  <input type="email" className="form-control" placeholder="you@example.com" value={form.email} onChange={(e) => updateForm('email', e.target.value)} required />
                </div>
              </div>
            </div>

            {isCollegePortal && (
              <div className="row g-3 mt-1 mb-2">
                <div className="col-12 col-md-6">
                  <label className="form-label" style={{ fontSize: 13 }}>College Validation Year <span className="text-danger">*</span></label>
                  <input className="form-control" list="college-validation-years" placeholder="2026" value={form.passedOutYear} onChange={(e) => updateForm('passedOutYear', e.target.value)} />
                  <datalist id="college-validation-years">
                    {(collegeAccessInfo?.acceptedYears || []).map((year) => <option key={year} value={year} />)}
                  </datalist>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label" style={{ fontSize: 13 }}>Department <span className="text-danger">*</span></label>
                  <input className="form-control" list="college-departments" placeholder="CSE / MCA" value={form.department} onChange={(e) => updateForm('department', e.target.value)} />
                  <datalist id="college-departments">
                    {(collegeAccessInfo?.acceptedDepartments || []).map((department) => <option key={department} value={department} />)}
                  </datalist>
                </div>
                <div className="col-12">
                  <label className="form-label" style={{ fontSize: 13 }}>College Student ID <span style={{ color: '#94a3b8' }}>(optional)</span></label>
                  <input className="form-control" placeholder="Register number or student ID" value={form.collegeStudentId} onChange={(e) => updateForm('collegeStudentId', e.target.value)} />
                </div>
                <div className="col-12">
                  <label className="form-label" style={{ fontSize: 13 }}>Upload College ID Proof <span className="text-danger">*</span></label>
                  <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Upload the ID proof issued by your college. Supported formats: JPG, PNG, WEBP, PDF.</div>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary w-100 fw-semibold mt-3">
              <i className="bi bi-arrow-right-circle me-2"></i>Continue to Verification
            </button>
          </>
        ) : (
          <>
            <div className="d-flex align-items-center justify-content-between mt-1 mb-3">
              <div>
                <div className="fw-semibold text-white">Contact verification</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Phone verification is optional. Set the password to finish registration.</div>
              </div>
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label" style={{ fontSize: 13 }}>Phone Number <span style={{ color: '#94a3b8' }}>(optional)</span></label>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-telephone"></i></span>
                  <input type="tel" className="form-control" placeholder="+91 9876543210" value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} />
                  <button type="button" className="btn btn-outline-primary" onClick={handleSendOtp} disabled={!canSendOtp}>
                    {otpLoading ? 'Sending...' : otpCooldown > 0 ? `Retry in ${otpCooldown}s` : (otpSent ? 'Resend OTP' : 'Send OTP')}
                  </button>
                </div>
                {otpSent && (
                  <div style={{ fontSize: 11, color: otpVerified ? '#4ade80' : '#94a3b8', marginTop: 6 }}>
                    {otpVerified ? `Phone verified for ${otpMaskedPhone || 'this number'}.` : `OTP sent to ${otpMaskedPhone || 'your phone number'}.`}
                  </div>
                )}
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label" style={{ fontSize: 13 }}>OTP Verification {hasPhoneNumber && <span className="text-danger">*</span>}</label>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-shield-lock"></i></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    disabled={!otpSent || otpVerified}
                  />
                  <button type="button" className="btn btn-outline-success" style={{ fontWeight: 700 }} onClick={handleVerifyOtp} disabled={!otpSent || otpVerified || otpVerifyLoading || otp.length !== 6}>
                    {otpVerifyLoading ? 'Verifying...' : otpVerified ? 'Verified' : 'Verify OTP'}
                  </button>
                </div>
              </div>

              <div className="col-12">
                <label className="form-label" style={{ fontSize: 13 }}>Password <span className="text-danger">*</span></label>
                <div className="input-group">
                  <span className="input-group-text"><i className="bi bi-lock"></i></span>
                  <input
                    type={showPass ? 'text' : 'password'}
                    className="form-control"
                    placeholder="At least 6 characters"
                    value={form.password}
                    onChange={(e) => updateForm('password', e.target.value)}
                    required
                    minLength={6}
                  />
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPass((prev) => !prev)} tabIndex={-1}>
                    <i className={`bi ${showPass ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary w-100 fw-semibold mt-3" disabled={loading}>
              {loading
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating Account...</>
                : <><i className="bi bi-person-check-fill me-2"></i>Create My Account</>}
            </button>
          </>
        )}
      </form>

      <div className="text-center mt-3" style={{ fontSize: 13 }}>
        Already have an account?{' '}
        <Link to={orgCode ? `/login?tenant=${encodeURIComponent(orgCode)}` : '/login'} className="fw-semibold">Sign in</Link>
      </div>
    </div>
  );
}
